import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { PageHeader } from '@/components/PageHeader';
import DropdownMenu from '@/components/DropdownMenu';
import PageTitleAndAction from '@/components/PageTitleAndActions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useApi, useAsync, useNavigate } from '@/lib/legacy';
import { notifyError } from '@/lib/notify';
import { Map as MapJs } from '../../ui';
import { getBounds } from '../../ui/utils';
import { apiErrorText, formatMoney } from '../../utils';
import { calculateTotalGrossWrittenPremium, getTotalAllInCostValue } from '../../utils/getTotalAllInCostValue';
import { QuoteStat as QuoteStatJs } from '../QuotePage/content/QuoteView/QuoteStat';
import LoaderModal from '../../organisms/MCEModals/LoaderModal';
import {
  PolicyPageContextProvider as PolicyPageContextProviderJs,
  usePolicyPageContext as usePolicyPageContextJs,
} from './context/PolicyPageContext';
import { PolicyTabs as PolicyTabsJs } from './content/PolicyView/PolicyTabs';
import { AhsRenewalDialog, type AhsPackage } from '@/components/AhsRenewalDialog';

/* The page's state lives in PolicyPageContext.jsx and the API in hooks/useApi.jsx, both JavaScript:
   these are the narrow shapes this file reads. */
type Fee = { id?: string; name: string; amount?: number; rateOfNet?: number | null };
type Location = { lat?: number; lon?: number; coverages: { netPremium: number }[] };
export type Policy = {
  id: number;
  insured: string;
  policyNumber?: string;
  product?: { name?: string };
  effectiveAt?: string;
  effectiveUntil?: string;
  canceledAt?: number | null;
  payoutsEnabled?: boolean;
  coveredValue?: number;
  quoteRate?: number;
  locations: Location[];
  fees: Fee[];
  timezone?: string;
} & Dated;
type Dated = { lastChangeAt?: string | number | null; createdAt?: string | number | null };
type Endorsement = { endorsementType?: string; endorsementData?: { parent?: { type?: string; id?: unknown } } };
type PolicyPageState = {
  policy?: Policy;
  status: string;
  fetchingPolicyDetails: boolean;
  monthlyReportingData: unknown;
  MCEsData: unknown[] | null;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  editMonthlyReporting: boolean;
  setEditMonthlyReporting: (edit: boolean) => void;
  handleUpdateMonthlyReportingData: () => void;
  handleResetMonthlyReportingData: () => void;
  isUpdatingMonthly: boolean;
  combinedMode: boolean;
  setCombinedMode: (update: (prev: boolean) => boolean) => void;
  setMonthlyReportingData: (data: unknown) => void;
  showMCEColumns: boolean;
  setShowMCEColumns: (show: boolean) => void;
  endorsements?: Endorsement[];
  downloadMonthlyStatementById: unknown;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: pages/PolicyPage/context/PolicyPageContext.jsx
const usePolicyPageContext = usePolicyPageContextJs as any as () => PolicyPageState;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: pages/PolicyPage/context/PolicyPageContext.jsx
const PolicyPageContextProvider = PolicyPageContextProviderJs as any as (props: { children: React.ReactNode }) => JSX.Element;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: PolicyTabs takes the page's props through to the tab views
const PolicyTabs = PolicyTabsJs as any as (props: Record<string, unknown>) => JSX.Element;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: QuotePage/content/QuoteView/QuoteStat.jsx (the stat band stays legacy, D34)
const QuoteStat = QuoteStatJs as any as (props: { data: { title: string; text: string; style?: string }[] }) => JSX.Element;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: ui/components/Mapbox (the legacy map stays, D6)
const Map = MapJs as any;

const AHS_PRODUCTS = ['Auto Hail-Safe-Max10', 'Auto Hail-Safe'];

const tabs = {
  COVERAGES: 'COVERAGES',
  MONTHLY_REPORTING: 'MONTHLY_REPORTING',
  MCES: 'MCES',
  ATTACHMENTS: 'ATTACHMENTS',
  NAMED_INSURED: 'NAMED_INSURED',
  HIGH_VALUE_VEHICLES: 'HIGH_VALUE_VEHICLES',
  TEMPORARY_LOCATIONS: 'TEMPORARY_LOCATIONS',
  FALSE_PRETENSE: 'FALSE_PRETENSE',
  LOSS_PAYEE: 'LOSS_PAYEE',
  INVENTORY_VALUE: 'INVENTORY_VALUE',
  POLICY_GENERATION: 'POLICY_GENERATION',
};

/** A download button. (No API response carries a download link, so there is no href to follow.) */
function DownloadButton({ loading, onClick, children }: { loading: boolean; onClick: () => void; children: string }) {
  return (
    <Button disabled={loading} onClick={onClick}>
      {loading ? <Spinner /> : null}
      {children}
    </Button>
  );
}

function PolicyDetails({ policy, setShowStatementsListModal }: { policy: Policy; setShowStatementsListModal: (show: boolean) => void }) {
  const navigate = useNavigate();
  const {
    currentTab,
    editMonthlyReporting,
    setEditMonthlyReporting,
    handleUpdateMonthlyReportingData,
    handleResetMonthlyReportingData,
    isUpdatingMonthly,
    combinedMode,
    setCombinedMode,
    setMonthlyReportingData,
    showMCEColumns,
    setShowMCEColumns,
    endorsements = [],
  } = usePolicyPageContext();
  const { regeneratePolicy, downloadPolicy, getPolicyMonthlyReportingData, createRenewalQuote, getPackages, getInferredPackage } = useApi();
  const [loading, setLoading] = useState(false);
  const [ahsRenewalOpen, setAhsRenewalOpen] = useState(false);
  const [packages, setPackages] = useState<AhsPackage[]>([]);
  const [inferredPackageId, setInferredPackageId] = useState<string | null>(null);

  const isAHS = AHS_PRODUCTS.includes(policy?.product?.name ?? '');

  const handleOpenAhsRenewal = useCallback(async () => {
    try {
      const [pkgs, inferred] = await Promise.all([getPackages(), getInferredPackage(policy.id)]);
      setPackages(pkgs || []);
      setInferredPackageId(inferred?.id || null);
    } catch (err) {
      setPackages([]);
      setInferredPackageId(null);
    }
    setAhsRenewalOpen(true);
  }, [getPackages, getInferredPackage, policy?.id]);

  const [loadingStates, setLoadingStates] = useState({ policyWithEndorsements: false, policyOnly: false });

  const daysRemaining = useMemo(() => {
    if (!policy?.effectiveUntil) return 0;
    return moment(policy.effectiveUntil).diff(moment(), 'days');
  }, [policy?.effectiveUntil]);

  const handleDownloadPolicy = useCallback(
    async ({ includeEndorsements }: { includeEndorsements: boolean }) => {
      const key = includeEndorsements ? 'policyWithEndorsements' : 'policyOnly';
      setLoadingStates((prev) => ({ ...prev, [key]: true }));
      try {
        const response = await downloadPolicy(policy.id, includeEndorsements);
        if (response) {
          window.open(response.data.value, '_blank');
        }
      } finally {
        setLoadingStates((prev) => ({ ...prev, [key]: false }));
      }
    },
    [policy.id, downloadPolicy]
  );

  const handleRegenPolicy = useCallback(
    async ({ includeEndorsements }: { includeEndorsements: boolean }) => {
      setLoading(true);
      try {
        return await regeneratePolicy(policy.id, includeEndorsements);
      } catch (err) {
        notifyError('Regenerate in progress... please wait until everything is processed.');
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [regeneratePolicy, policy.id]
  );

  // Today's callback closes over the first render (an empty dependency list); kept as it is.
  const handleCreateRenewal = useCallback(async () => {
    setLoading(true);
    try {
      if (daysRemaining > 90) {
        return undefined;
      }
      const response = await createRenewalQuote(policy.id);
      if (response) {
        navigate.to(`/quotes/${response.id}`);
      }
      return response;
    } catch (err) {
      // A refusal (409) or a pricing failure (500) answers with a message naming what to fix.
      notifyError(apiErrorText(err, 'Renewal Quote failed', { serverErrorText: true }));
      return undefined;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { execute: regenPolicy } = useAsync(handleRegenPolicy, { immediate: false });
  const { execute: createRenewal } = useAsync(handleCreateRenewal, { immediate: false });

  const actionButtons =
    currentTab !== 'MONTHLY_REPORTING' ? (
      <div className="flex flex-row flex-wrap items-center justify-end gap-1.5">
        <DownloadButton loading={loadingStates.policyWithEndorsements} onClick={() => handleDownloadPolicy({ includeEndorsements: true })}>
          Download Policy + Endorsements
        </DownloadButton>
        <DownloadButton loading={loadingStates.policyOnly} onClick={() => handleDownloadPolicy({ includeEndorsements: false })}>
          Download Policy
        </DownloadButton>
        <DropdownMenu
          options={[
            { label: 'Submit Claim', onClick: () => navigate.to('/submitclaim') },
            { label: 'Regenerate Policy', onClick: () => regenPolicy({ includeEndorsements: false }) },
            { label: 'Regenerate Policy Incl. Endorsements', onClick: () => regenPolicy({ includeEndorsements: true }) },
            isAHS
              ? { label: 'Renew AHS Policy', onClick: () => handleOpenAhsRenewal(), disabled: daysRemaining > 90 }
              : { label: 'Create Renewal Quote', onClick: () => createRenewal(), disabled: daysRemaining > 90 },
          ]}
        />
      </div>
    ) : (
      <div className="flex flex-row flex-wrap items-center justify-end gap-3">
        <Button onClick={() => setShowMCEColumns(!showMCEColumns)}>
          {showMCEColumns ? 'Hide Location Change MCEs' : 'Show Location Change MCEs'}
        </Button>
        <Button onClick={() => setShowStatementsListModal(true)}>Quarterly Statements</Button>
        <Button
          disabled={editMonthlyReporting}
          onClick={async () => {
            try {
              setCombinedMode((prev) => !prev);
              const data = await getPolicyMonthlyReportingData(policy.id, combinedMode);
              if (!data) {
                throw new Error('No data received');
              }
              setMonthlyReportingData(data);
            } catch (error) {
              setCombinedMode((prev) => !prev);
              notifyError('Failed to Uncombine monthly reporting data.');
            }
          }}
        >
          {combinedMode ? 'Show Uncombined' : 'Show Combined'}
        </Button>
        {!editMonthlyReporting ? (
          <Button disabled={!combinedMode} onClick={() => setEditMonthlyReporting(true)}>
            Edit
          </Button>
        ) : (
          <div className="flex flex-row items-center gap-1">
            <Button
              onClick={() => {
                setEditMonthlyReporting(false);
                handleResetMonthlyReportingData();
              }}
            >
              Cancel
            </Button>
            <Button disabled={isUpdatingMonthly} onClick={() => handleUpdateMonthlyReportingData()}>
              {isUpdatingMonthly ? <Spinner /> : null}
              Save
            </Button>
          </div>
        )}
      </div>
    );

  const totalGrossPremium = calculateTotalGrossWrittenPremium(policy?.locations);

  const policyData = [
    { title: 'Gross Written Premium', text: `${formatMoney(totalGrossPremium, { prefix: '$', withDecimals: true })}` },
    { title: 'Total All-In-Cost', text: `${formatMoney(getTotalAllInCostValue(policy), { prefix: '$', withDecimals: true })}` },
    {
      title: 'Rate',
      text: policy.quoteRate
        ? `$ ${policy.quoteRate}`
        : policy?.coveredValue
        ? `$ ${((totalGrossPremium / policy.coveredValue) * 100).toFixed(4)}`
        : 'N/A',
    },
    { title: '∑ Avg Total Insured Value', text: `${formatMoney(policy.coveredValue, { prefix: '$', withDecimals: true })}`, style: 'dot' },
  ];

  const status = policy.canceledAt != null && policy.canceledAt <= Date.now() ? 'Cancelled' : policy.payoutsEnabled ? 'Active' : 'Inactive';

  // The stacking is verify's: this block is a stacking context (react-native-web's View was
  // relative, z-index 0) and the stat band sits under the rest of it (LayoutBox zIndex={-1}).
  return (
    <div className="relative z-0 flex flex-col gap-4 px-10">
      <PageTitleAndAction
        data={policy}
        HeaderTitle={policy.insured}
        showHeroIcon={false}
        status={status}
        itemNumber={policy.policyNumber && `Policy No. #${policy.policyNumber}`}
        actionButtons={actionButtons}
        expiration={{ text: `Expires ${moment(policy.effectiveUntil).format('MM/DD/YYYY')}` }}
        effectiveDate={policy.effectiveAt}
        hasPolicyIVE={endorsements?.some((e) => {
          if (e?.endorsementType !== 'invEndorsement') return false;
          const parent = e?.endorsementData?.parent;
          return parent?.type === 'policy' && String(parent?.id) === String(policy.id);
        })}
      />
      <div className="relative z-[-1] w-full">
        <QuoteStat data={policyData} />
      </div>
      {loading ? <LoaderModal loading={loading} setLoading={setLoading} /> : null}
      {isAHS ? (
        <AhsRenewalDialog
          policy={policy}
          open={ahsRenewalOpen}
          packages={packages}
          inferredPackageId={inferredPackageId}
          onClose={() => setAhsRenewalOpen(false)}
          onSuccess={(renewedPolicy) => {
            setAhsRenewalOpen(false);
            navigate.to(`/policies/${renewedPolicy.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function FeesSection({ fees }: { fees: Fee[] }) {
  return (
    <div className="flex w-full flex-col bg-white p-6 text-page-meta-strong">
      <div>
        <span className="text-base">Fees</span>
      </div>
      <div className="flex flex-col items-center gap-3 self-stretch bg-white py-2">
        {fees.map((fee) => (
          <div key={fee.name} className="flex w-full flex-row bg-white">
            <div className="flex w-1/2 flex-row">
              <span className="text-sm">{fee.rateOfNet ? `${fee.name} - ${fee.rateOfNet}%` : fee.name}</span>
            </div>
            <div className="flex w-1/2 flex-row">
              <span className="text-sm">{`${formatMoney(fee?.amount, { prefix: '$', withDecimals: true })}`}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SitesMap({ sites }: { sites: Location[] }) {
  if (!sites || !sites.length) {
    return null;
  }
  return (
    <div className="relative h-[300px] w-full">
      <LocationMap sites={sites} />
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[99] flex flex-row items-center justify-center pt-[250px]">
        <Badge className="h-auto rounded-xl bg-brand-orange px-4 py-2 text-xs font-semibold text-foreground uppercase">
          <span className="font-light">Station Status:</span> Install ticket created
        </Badge>
      </div>
    </div>
  );
}

function LocationMap({ sites }: { sites: Location[] }) {
  const site = sites.length ? sites[0] : null;
  const { bounds, maxBounds } = useMemo(() => {
    if (site && site.lat && site.lon) {
      const { lat, lon } = site;
      return {
        bounds: getBounds({ center: [lon, lat], hectares: 1 }),
        maxBounds: getBounds({ center: [lon, lat], hectares: 15 }),
      };
    }
    return { bounds: null, maxBounds: null };
  }, [site]);
  const markerCoordinate = useMemo(() => (site ? [site.lon, site.lat] : null), [site]);
  if (!site) {
    return null;
  }
  return (
    <Map staticMap style={{ width: '100%', height: '100%' }}>
      <Map.Camera bounds={bounds} maxBounds={maxBounds} setBoundsOnResize={false} animationDuration={400} />
      {markerCoordinate ? <Map.Marker id="mapmarker" coordinate={markerCoordinate} /> : null}
    </Map>
  );
}

function Policy() {
  const { currentTab, setCurrentTab, policy, monthlyReportingData, status, fetchingPolicyDetails, MCEsData, downloadMonthlyStatementById } =
    usePolicyPageContext();
  const [showStatementsListModal, setShowStatementsListModal] = useState(false);

  const sites = useMemo(() => (policy && Array.isArray(policy.locations) ? [...policy.locations] : []), [policy]);

  useEffect(() => {
    setCurrentTab('COVERAGES');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PageHeader back={{ to: '/policies', label: policy?.policyNumber ? `Policies/ #${policy.policyNumber}` : 'Back to Policies' }} />
      <div className="flex min-h-full w-full flex-1 flex-col bg-page-surface">
        {status === 'pending' || fetchingPolicyDetails ? (
          <div className="flex min-h-screen w-full items-center justify-center">
            <Spinner className="size-8" />
          </div>
        ) : null}
        {status === 'error' ? <p>Could not get policy information. Try refreshing the page.</p> : null}
        {policy && monthlyReportingData && MCEsData ? (
          <div className="flex w-full flex-col gap-10">
            <SitesMap sites={sites} />
            <PolicyDetails policy={policy} setShowStatementsListModal={setShowStatementsListModal} />
            <PolicyTabs
              data={policy}
              productCoverages={policy.locations}
              policyTabsList={tabs}
              quotesView={false}
              monthlyReportingData={monthlyReportingData}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              MCEsData={MCEsData}
              setShowStatementsListModal={setShowStatementsListModal}
              showStatementsListModal={showStatementsListModal}
              downloadMonthlyStatementById={downloadMonthlyStatementById}
            />
            {currentTab === 'COVERAGES' && !!policy.fees.length ? <FeesSection fees={policy.fees} /> : null}
          </div>
        ) : null}
      </div>
    </>
  );
}

export const PolicyPage = () => (
  <PolicyPageContextProvider>
    <Policy />
  </PolicyPageContextProvider>
);
